import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../../../../components/ui/Button';
import { Badge } from '../../../../components/ui/Badge';
import { Input } from '../../../../components/ui/Input';
import { Question } from '../../../../content';
import { QuestionPreviewPanel } from './QuestionPreviewPanel';
interface QuestionGroup {
  id: string;
  label: string;
  questions: Question[];
}
interface QuestionBrowserProps {
  title: string;
  subtitle?: string;
  questions: Question[];
  onBack: () => void;
  accentColor?: 'blue' | 'purple' | 'emerald' | 'amber';
  /** Group questions by familyId or show flat list */
  groupBy?: 'family' | 'none';
  /** Additional stats to show in header */
  headerStats?: React.ReactNode;
  selectedQuestionId?: string | null;
  onSelectQuestion?: (questionId: string | null) => void;
  onNavigateToGovernance?: (questionId: string) => void;
}
const ACCENT_COLORS = {
  blue: {
    selected: 'bg-blue-50 border-l-blue-500',
    hover: 'hover:bg-blue-50/50'
  },
  purple: {
    selected: 'bg-purple-50 border-l-purple-500',
    hover: 'hover:bg-purple-50/50'
  },
  emerald: {
    selected: 'bg-emerald-50 border-l-emerald-500',
    hover: 'hover:bg-emerald-50/50'
  },
  amber: {
    selected: 'bg-amber-50 border-l-amber-500',
    hover: 'hover:bg-amber-50/50'
  }
};
export function QuestionBrowser({
  title,
  subtitle,
  questions,
  onBack,
  accentColor = 'blue',
  groupBy = 'family',
  headerStats,
  selectedQuestionId: selectedQuestionIdProp,
  onSelectQuestion,
  onNavigateToGovernance
}: QuestionBrowserProps) {
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(selectedQuestionIdProp ?? null);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    if (selectedQuestionIdProp === undefined) return;
    const isValidSelection = questions.some(question => question.id === selectedQuestionIdProp);
    setSelectedQuestionId(isValidSelection ? selectedQuestionIdProp : null);
  }, [questions, selectedQuestionIdProp]);
  const selectQuestion = (questionId: string | null) => {
    setSelectedQuestionId(questionId);
    onSelectQuestion?.(questionId);
  };
  const colors = ACCENT_COLORS[accentColor];
  // Filter questions by search
  const filteredQuestions = useMemo(() => {
    if (!searchQuery.trim()) return questions;
    const query = searchQuery.toLowerCase();
    return questions.filter(q => q.question.toLowerCase().includes(query) || q.id.toLowerCase().includes(query) || q.familyId?.toLowerCase().includes(query));
  }, [questions, searchQuery]);
  // Group questions
  const groups = useMemo((): QuestionGroup[] => {
    if (groupBy === 'none') {
      return [{
        id: 'all',
        label: `Alle vragen (${filteredQuestions.length})`,
        questions: filteredQuestions
      }];
    }
    // Group by familyId
    const byFamily: Record<string, Question[]> = {};
    filteredQuestions.forEach(q => {
      const familyId = q.familyId || 'unknown';
      if (!byFamily[familyId]) byFamily[familyId] = [];
      byFamily[familyId].push(q);
    });
    return Object.entries(byFamily).map(([familyId, qs]) => ({
      id: familyId,
      label: familyId,
      questions: qs
    })).sort((a, b) => a.id.localeCompare(b.id));
  }, [filteredQuestions, groupBy]);
  return <div className="flex flex-col h-full bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6175" data-mp="3sb70fs915jc9">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-6176" data-mp="3nc2tl89kvxp3">
        <div className="flex items-center gap-4 min-w-0" data-id="element-6177" data-mp="3ij1cqupt9o2v">
          <Button variant="secondary" size="sm" onClick={onBack} data-id="element-6178" data-mp="3dmj7ltqkjnq0">
            <ChevronLeft className="w-4 h-4 sm:mr-1" data-id="element-6179" data-mp="evr5x1r1qnlc" />
            <span className="hidden sm:inline" data-id="element-6180" data-mp="ewj14bgqejb3">Terug</span>
          </Button>
          <div className="min-w-0" data-id="element-6181" data-mp="a4sm3s6un7dc">
            <h2 className="text-lg font-bold text-gray-900 truncate" data-id="element-6182" data-mp="a3q09yodvv4a">
              {title}
            </h2>
            {subtitle && <p className="text-sm text-gray-500 truncate" data-id="element-6183" data-mp="3qurttrnivm5b">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-6184" data-mp="3gu94ihd6vdg0">
          {headerStats}
          <Badge variant="info" data-id="element-6185" data-mp="3gwlw7m8r1269">{questions.length} vragen</Badge>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden" data-id="element-6186" data-mp="ddittp6nwc7z">
        {/* Left sidebar - Question list */}
        <div className={`w-full lg:w-1/3 border-r border-gray-200 flex flex-col bg-white min-h-0 ${selectedQuestionId ? 'hidden lg:flex' : 'flex'}`} data-id="element-6187" data-mp="icvga8tmfha1">
          
          {/* Search */}
          <div className="p-3 border-b border-gray-100 flex-shrink-0" data-id="element-6188" data-mp="8mivcfhw9m3d">
            <Input type="text" placeholder="Zoek in vragen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-6190" data-mp="112tp4r58teeq" />} variant="admin" size="sm" data-id="element-6189" data-mp="8kfx586l8mnk" />
            
            {searchQuery && <div className="text-xs text-gray-400 mt-1.5" data-id="element-6191" data-mp="2lt8qelr85ec6">
                {filteredQuestions.length} van {questions.length} resultaten
              </div>}
          </div>

          {/* Question list */}
          <div className="flex-1 overflow-y-auto" style={{
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-6192" data-mp="2vjtng4r92asz">
            
            {groups.map(group => <div key={group.id} data-id="element-6193" data-mp="2ql0kv7di447l">
                {/* Group header (only show if grouping) */}
                {groupBy !== 'none' && <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 sticky top-0 z-10" data-id="element-6194" data-mp="22whcysyd5tg1">
                    <div className="flex items-center justify-between" data-id="element-6195" data-mp="1xxwd4un0xbs4">
                      <span className="text-xs font-mono font-bold text-gray-700 truncate" data-id="element-6196" data-mp="2bwno486lahc1">
                        {group.label}
                      </span>
                      <span className="text-xs text-gray-500 flex-shrink-0 ml-2" data-id="element-6197" data-mp="3n9q4p38ov6w7">
                        {group.questions.length} vragen
                      </span>
                    </div>
                  </div>}

                {/* Questions in group */}
                {group.questions.map(q => <motion.button key={q.id} onClick={() => selectQuestion(q.id)} className={`w-full text-left p-4 border-b border-gray-100 transition-colors border-l-4 ${selectedQuestionId === q.id ? colors.selected : `border-l-transparent ${colors.hover}`}`} whileTap={{
              scale: 0.99
            }} data-id="element-6198" data-mp="2kwtl73s3syng">
                
                    <div className="flex items-center gap-2 mb-1.5" data-id="element-6199" data-mp="1x8k76o14tf6v">
                      <span className="text-[10px] font-mono font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-6200" data-mp="2262fcc7brqdt">
                        {(() => {
                    // Show the distinguishing tail of the id.
                    // Family ids are dash-separated (e.g. TOPO-NL-STEDEN);
                    // anything after that prefix uniquely identifies the question.
                    // For TOPO-NL-STEDEN-015 → "015", for TOPO-NL-STEDEN-MAP-005 → "MAP-005".
                    const parts = q.id.split('-');
                    const familyParts = (q.familyId ?? '').split('-');
                    const tail = parts.slice(familyParts.length).join('-');
                    return tail || parts[parts.length - 1];
                  })()}
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-6201" data-mp="wzavn3piehin">
                        Tier {q.tier}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-800 line-clamp-2 leading-snug" data-id="element-6202" data-mp="s1smbc7ky0a4">
                      {q.question}
                    </p>
                  </motion.button>)}
              </div>)}

            {filteredQuestions.length === 0 && <div className="p-8 text-center text-gray-500 text-sm" data-id="element-6203" data-mp="i820yg6r25yn">
                {searchQuery ? 'Geen vragen gevonden voor deze zoekopdracht.' : 'Geen vragen beschikbaar.'}
              </div>}
          </div>
        </div>

        {/* Right panel - Question preview */}
        <div className={`w-full lg:w-2/3 flex flex-col min-h-0 ${!selectedQuestionId ? 'hidden lg:flex' : 'flex'}`} data-id="element-6204" data-mp="4b5cgle3t0kx">
          
          <QuestionPreviewPanel questionId={selectedQuestionId} onBack={() => selectQuestion(null)} accentColor={accentColor} showBackOnMobile={true} onNavigateToGovernance={onNavigateToGovernance} data-id="element-6205" data-mp="1s5ids0j6j3oq" />
          
        </div>
      </div>
    </div>;
}